import { Controller } from "@hotwired/stimulus"
import { post, get } from "@rails/request.js"

const BULK_KINDS = {
  full_crown: { checkbox: "selected_teeth[]", priority: 0 },
  crown_face: { checkbox: "selected_faces[]", priority: 1 }
}

// Connects to data-controller="shortcut-multiple-select"
export default class extends Controller {
  static targets = ["face", "trigger"]
  static values = {
    bulkFormUrl: String,
    bulkCreateUrl: String,
    bulkFormFullCrownUrl: String,
    bulkCreateFullCrownUrl: String,
    selectedItems: { type: Number, default: 0 },
    mode: { type: String, default: "direct" } // "direct" o "form"
  }

  connect() {
    this.#updateSelectedItemsValue()
    this.shiftPressed = false

    this._handleKeyDown = this.#handleKeyDown.bind(this)
    this._handleKeyUp   = this.#handleKeyUp.bind(this)
    this._handleFrameRender = this.#handleFrameRender.bind(this)

    document.addEventListener("keydown", this._handleKeyDown)
    document.addEventListener("keyup", this._handleKeyUp)
    document.addEventListener("turbo:frame-render", this._handleFrameRender)
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleKeyDown)
    document.removeEventListener("keyup", this._handleKeyUp)
    document.removeEventListener("turbo:frame-render", this._handleFrameRender)
  }

  toggle(event) {
    if (!event.shiftKey) return

    event.preventDefault()
    event.stopPropagation()

    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
    const link = event.currentTarget

    if (!checkbox) return

    const kind = this.#kindForCheckbox(checkbox)
    if (!kind) return

    if (this.#otherKindsWithSelections(kind).length > 0) {
      this.#clearOtherKinds(kind)
    }

    checkbox.checked = !checkbox.checked
    this.#updateFaceSelection(link.querySelector('[data-shortcut-multiple-select-target="face"]'), checkbox.checked)
    this.#updateTriggerSelection(link, checkbox.checked)
    this.#updateSelectedItemsValue()
  }

  selectedItemsValueChanged() {
    this.element.classList.toggle("visible", this.selectedItemsValue !== 0)
  }

  #handleKeyDown(event) {
    if (event.key === "Shift") this.shiftPressed = true
  }

  #handleKeyUp(event) {
    if (!this.element.isConnected) return

    if (event.key === "Shift" && this.shiftPressed) {
      this.shiftPressed = false
      this.#processBulkSelection()
    }
  }

  async #processBulkSelection() {
    const kind = this.#activeBulkKind()
    if (!kind) return

    const payload = this.#payloadFor(kind)
    const action = this.modeValue === "direct" ? "create" : "form"
    await this.#bulkRequest(kind, action, payload)
  }

  #activeBulkKind() {
    return Object.entries(BULK_KINDS)
      .sort(([, a], [, b]) => a.priority - b.priority)
      .map(([kind]) => kind)
      .find(kind => this.#selectedCheckboxes(kind).length > 0)
  }

  async #bulkRequest(kind, action, payload) {
    const url = this.#bulkEndpoints[kind]?.[action]
    if (!url) return

    try {
      const response = action === "create"
        ? await post(url, {
            body: JSON.stringify({ selections: this.#createBody(kind, payload) }),
            contentType: "application/json",
            responseKind: "turbo-stream"
          })
        : await get(this.#formUrl(url, payload), { responseKind: "turbo-stream" })

      if (response.ok && action === "create") this.clearSelections()
    } catch (error) {
      console.error(`Error en bulk ${action} (${kind}):`, error)
    }
  }

  #createBody(kind, payload) {
    if (kind === "full_crown") {
      return payload.map(toothId => ({ tooth_id: toothId }))
    }

    return Object.keys(payload).map(toothId => ({
      tooth_id: toothId,
      faces: payload[toothId]
    }))
  }

  #formUrl(url, payload) {
    const selectionsParam = encodeURIComponent(JSON.stringify(payload))
    const separator = url.includes("?") ? "&" : "?"
    return `${url}${separator}selections=${selectionsParam}`
  }

  #payloadFor(kind) {
    const checkboxes = this.#selectedCheckboxes(kind)

    if (kind === "full_crown") {
      return checkboxes.map(checkbox => checkbox.value)
    }

    return checkboxes.reduce((selections, checkbox) => {
      const [toothId, face] = checkbox.value.split("-")
      selections[toothId] ||= []
      selections[toothId].push(face)
      return selections
    }, {})
  }

  #selectedCheckboxes(kind) {
    const name = BULK_KINDS[kind].checkbox
    return [...this.element.querySelectorAll(`input[type="checkbox"][name="${name}"]`)].filter(checkbox => checkbox.checked)
  }

  get #bulkEndpoints() {
    return {
      crown_face: { form: this.bulkFormUrlValue, create: this.bulkCreateUrlValue },
      full_crown: { form: this.bulkFormFullCrownUrlValue, create: this.bulkCreateFullCrownUrlValue }
    }
  }

  #updateSelectedItemsValue() {
    this.selectedItemsValue = Object.keys(BULK_KINDS).reduce((count, kind) => {
      return count + this.#selectedCheckboxes(kind).length
    }, 0)
  }

  #updateFaceSelection(faceDiv, isSelected) {
    if (!faceDiv) return

    faceDiv.classList.toggle("face-selected", isSelected)
    faceDiv.style.boxShadow = isSelected ? "inset 0 0 0 1000px rgba(59, 130, 246, 0.4)" : ""
  }

  #updateTriggerSelection(link, isSelected) {
    if (!link.querySelector('[data-shortcut-multiple-select-target="trigger"]')) return

    link.classList.toggle("ring-2", isSelected)
    link.classList.toggle("ring-sky-400", isSelected)
  }

  clearSelections() {
    Object.keys(BULK_KINDS).forEach(kind => this.#clearKind(kind))
    this.#updateSelectedItemsValue()
  }

  #kindForCheckbox(checkbox) {
    return Object.entries(BULK_KINDS).find(([, config]) => config.checkbox === checkbox.name)?.[0]
  }

  #otherKindsWithSelections(activeKind) {
    return Object.keys(BULK_KINDS).filter(kind => kind !== activeKind && this.#selectedCheckboxes(kind).length > 0)
  }

  #clearOtherKinds(activeKind) {
    this.#otherKindsWithSelections(activeKind).forEach(kind => this.#clearKind(kind))
  }

  #clearKind(kind) {
    this.#selectedCheckboxes(kind).forEach(checkbox => {
      checkbox.checked = false
      const link = checkbox.closest("a")
      if (!link) return

      this.#updateFaceSelection(link.querySelector('[data-shortcut-multiple-select-target="face"]'), false)
      this.#updateTriggerSelection(link, false)
    })
  }

  handleFormSubmit(event) {
    if (event.detail.success) this.clearSelections()
  }

  #handleFrameRender(event) {
    const frame = event.target
    if (frame.id !== "selected-form-content") return

    const isBulkForm = frame.innerHTML.includes("Tratamiento Múltiple de Coronas")
    if (!isBulkForm && this.selectedItemsValue > 0) this.clearSelections()
  }
}
